import React, { FC } from 'react';
import styled from 'styled-components';
import {
  getOptionValue,
  getProductOptionSets,
  optionValueMatches,
  SelectedProductOptions,
} from './productOptionsHelpers';

const OptionsContainer = styled.div`
  margin: 20px 0;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 4px;

  @media (max-width: 767px) {
    margin: 15px 0;
    padding: 12px;
  }
`;

const OptionSetWrapper = styled.div`
  margin-bottom: 15px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #4caf50;
  }

  @media (max-width: 767px) {
    padding: 10px 12px;
    font-size: 16px; /* Prevents zoom on iOS */
  }
`;

const SelectedOption = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  font-size: 14px;
  color: #666;
`;

const SelectedLabel = styled.span`
  font-weight: 500;
  color: #333;
  margin-right: 4px;
`;

const DescriptionText = styled.p`
  a {
    color: #0066cc;
    text-decoration: underline;
  }
`;

interface ProductOptionFieldsProps {
  // Any product shape getProductOptionSets reads (new option sets or legacy
  // product_options).
  product: unknown;
  selectedOptions: SelectedProductOptions;
  onChange: (optionSetKey: string, optionValue: string) => void;
  // Keeps the fields' ids unique when several products show options at once.
  idPrefix?: string;
}

// One select per option set of a product, for whichever store holds the
// choice: the configured product's slice (ProductOptionsSelector) or a screen's
// own state (the retouching picker, one product per level).
const ProductOptionFields: FC<ProductOptionFieldsProps> = ({
  product,
  selectedOptions,
  onChange,
  idPrefix = '',
}) => {
  const allOptionSets = getProductOptionSets(product);

  if (allOptionSets.length === 0) {
    return null;
  }

  return (
    <OptionsContainer>
      {allOptionSets.map((optionSet) => {
        const selectedOption = selectedOptions?.[optionSet.key];
        const selectedOptionData = optionSet.product_options?.find((opt) =>
          optionValueMatches(opt, selectedOption),
        );
        const fieldId = `${idPrefix}option-set-${optionSet.id}`;

        return (
          <OptionSetWrapper key={optionSet.id}>
            <Label htmlFor={fieldId}>
              {optionSet.display_name || optionSet.name || 'Product Option'}
            </Label>
            {optionSet.description && (
              <DescriptionText
                dangerouslySetInnerHTML={{ __html: optionSet.description }}
              />
            )}
            <Select
              id={fieldId}
              name={fieldId}
              value={selectedOption || ''}
              onChange={(e) => onChange(optionSet.key, e.target.value)}
            >
              {!optionSet.selection_required && <option value="">None</option>}
              {optionSet.product_options?.map((option) => {
                const priceDisplay = option.price
                  ? ` (+$${(option.price / 100).toFixed(2)})`
                  : '';
                return (
                  <option key={option.id} value={getOptionValue(option)}>
                    {option.name}
                    {priceDisplay}
                    {option.is_default ? ' (default)' : ''}
                  </option>
                );
              })}
            </Select>

            {selectedOptionData && (
              <SelectedOption>
                <SelectedLabel>Selected:</SelectedLabel>
                {selectedOptionData.name}
                {(selectedOptionData.price ?? 0) > 0 && (
                  <span style={{ marginLeft: '8px' }}>
                    (+${((selectedOptionData.price ?? 0) / 100).toFixed(2)})
                  </span>
                )}
              </SelectedOption>
            )}
          </OptionSetWrapper>
        );
      })}
    </OptionsContainer>
  );
};

export default ProductOptionFields;
